import React from 'react'

import { Form } from '../../account/components/forms/FieldFactory'
import { BannerState } from '../../src/constants/enums'
import { setNotificationBanner } from '../../src/graphql/cache'
import { getPreferredLanguage } from '../../src/lib/storage'
import { LanguageCodes } from '../constants/enums'

export interface Customer {
  cirrusLogo: string
  clientId: string
  clientSecret: string
  customerName: string
  emptyStatePatientGoalsText: string
  enableTextMessageMarketing: boolean
  favicon: string
  faq: string | null
  informedConsent: string | null
  isCredentialsEditable: boolean
  isI18nEnabled: boolean
  isPatientSettingsVisible: boolean
  isRealTimeTranslationEnabled: boolean
  key: string
  noStreamAccessMessage: string | null
  noticeOfPrivacyPractices: string | null
  pageTitle: string
  patientConcernHotline: string | null
  patientFinancialResponsibility: string | null
  patientOnboardingEnabled: boolean
  primaryColor: string
  primaryLogo: string
  primaryLogoDark: string
  privacyPolicy: string
  registration: string | null
  sso: {
    heartbeatInterval: number
    heartbeatSameOrigin: boolean
    heartbeatTimeout: number
    heartbeatUrl: string
    legalSplashText: string
    signOutUrl: string
  } | null
  supportEmail: string
  termsAndAgreementsEnabled: boolean
  termsOfService: string
}

export interface EnvConfig {
  assetVersion: string
  environmentName: string
  erxProviderSsoPath: string | null
  fedrampEnabled: boolean
  heartbeatPath: string
  inactivityTimeout: number
  inferenceMappings: { heading: string; inference_name: string }[]
  mlServiceSecret: string | null
  mlServiceUri: string | null
  providerDeleteMessagesThresholdMinutes: number
  providerEditProgressNoteThresholdMinutes: number
  requestFromProviderDomain: boolean
  rollbarJsAccessToken: string | null
  websocketUrl: string | null
}

export interface SupportConfig {
  supportFooterText: string
  supportFooterHref: string
}

export interface User {
  id: number
  name: string
  preferredLanguage: LanguageCodes
  isPatient: boolean
  isProvider: boolean
}

export interface Configuration {
  customer: Customer
  envConfig: EnvConfig
  featureFlags: Record<string, Boolean>
  registrationConfig: Form[] | null
  supportConfig: SupportConfig
  systemMessage: {
    message: string
  } | null
  user: User
}

export const ConfigurationContext = React.createContext<
  Configuration | undefined
>(undefined)

interface Props {
  children: React.ReactNode
}

export const ConfigurationProvider: React.FC<Props> = (props) => {
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [value, setValue] = React.useState<Configuration>()

  React.useEffect(() => {
    const { access_token } = window.CMD?.user ?? {}
    const preferredLanguage = getPreferredLanguage()
    const headers = {
      Accept: 'application/json',
    }

    if (access_token) {
      headers['Authorization'] = `Bearer ${access_token}`
    }
    if (preferredLanguage) {
      headers['Accept-Language'] = preferredLanguage
    }

    fetch('/configuration', {
      headers,
    })
      .then((response) => response.json())
      .then((data: any) => {
        const systemMessage = data?.data?.attributes?.systemMessage?.message
        if (systemMessage) {
          setNotificationBanner({
            state: BannerState.WARNING,
            text: systemMessage,
            // null will keep the banner around until the close x is clicked
            timeout: null,
          })
        }
        setValue(data?.data?.attributes)
        setIsLoading(false)
      })
      .catch((_e) => {
        // console.log('error', e)
      })
  }, [])

  if (!isLoading && value) {
    return <ConfigurationContext.Provider value={value} {...props} />
  }
  return null
}
